import { useQueryClient } from '@tanstack/react-query'
import { userRole } from 'enums/backend'
import { jwtDecode } from 'jwt-decode'
import React, {
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import { decodedIdTokenType } from 'types/backend'

interface userType {
  email: string
  role: userRole
  name: string
}
export interface AuthContextType {
  isLoggedIn: boolean
  isAdmin: boolean
  login: (
    token: string,
    refreshToken: string,
    idToken: string,
  ) => void | Promise<void>
  logout: () => void
  user: null | userType
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  isAdmin: false,
  login: () => {},
  logout: () => {},
  user: null,
  isLoading: false,
})

export const useAuth = () => useContext(AuthContext)

interface AuthProviderProps {
  children: ReactElement
}

export const decodeIdToken: (idToken: string) => userType | null = idToken => {
  const jwt = jwtDecode(idToken) as decodedIdTokenType

  return {
    sub: jwt.sub,
    email: jwt.email,
    name: jwt.name,
    role: jwt.role,
  }
}
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [user, setUser] = useState<AuthContextType['user']>(null)
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const checkAuthentication = () => {
    const token = localStorage.getItem('token')
    const idToken = localStorage.getItem('id_token')
    if (token && idToken) {
      try {
        const jwt = decodeIdToken(idToken)
        setUser(jwt)
        setIsLoggedIn(true)
        if (jwt !== null && jwt.role === userRole.admin) setIsAdmin(true)
      } catch (error) {
        setIsLoggedIn(false)
      }
    }
  }

  useEffect(() => {
    checkAuthentication()
  }, [])

  const login: AuthContextType['login'] = (token, refreshToken, idToken) => {
    localStorage.setItem('token', token)
    localStorage.setItem('refresh_token', refreshToken)
    localStorage.setItem('id_token', idToken)
    try {
      const jwt = decodeIdToken(idToken)

      setUser(jwt)
      setIsLoggedIn(true)
      if (jwt !== null && jwt.role === userRole.admin) setIsAdmin(true)
    } catch (error) {
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('id_token')
      throw error
    }
  }

  const logout: AuthContextType['logout'] = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('id_token')
    setIsLoggedIn(false)
    setUser(null)
    setIsAdmin(false)
    setIsLoading(false)
    queryClient.removeQueries()
  }

  return (
    <AuthContext.Provider
      value={{
        isAdmin,
        isLoggedIn,
        login,
        logout,
        user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
